import React, { useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  List,
  Space,
  Card,
  Form,
  Input,
  Select,
  Typography,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CardType } from "../../../types/dashboard";
import {
  AnomalyPreview,
  CriticalMetricPreview,
  GeoClusterPreview,
  HeatmapPreview,
  HistogramPreview,
  LocationMapPreview,
  ProgressPreview,
  TreemapPreview,
  TrendAnalysisPreview,
  WordCloudPreview,
} from "../InsightCard/InsightPreviewCards";

const { Text } = Typography;

type ColumnOption = {
  name: string;
  dtype: string;
  unique_values?: any[];
  sample_values?: any[];
  min?: number;
  max?: number;
};

type Props = {
  open: boolean;
  selectedType: CardType | null;
  setSelectedType: (t: CardType | null) => void;
  textTemp: string;
  setTextTemp: (v: string) => void;
  onAdd: () => void;
  onClose: () => void;
  loading?: boolean;
  cardLabels: Record<
    CardType,
    { label: string; render: (c: any) => React.ReactNode }
  >;

  columns: ColumnOption[];
  selectedMetric: string | undefined;
  setSelectedMetric: (v: string | undefined) => void;
  selectedDimension: string | undefined;
  setSelectedDimension: (v: string | undefined) => void;
  selectedFilterColumn: string | undefined;
  setSelectedFilterColumn: (v: string | undefined) => void;
  selectedFilterValues: string[];
  setSelectedFilterValues: (v: string[]) => void;
  selectedImageFile: File | null;
  setSelectedImageFile: (file: File | null) => void;
  uploadedImageUrl: string;
  setUploadedImageUrl: (url: string) => void;
  goToDashboard: () => void;

  customCardTitle: string;
  setCustomCardTitle: (value: string) => void;
  isBscPinned?: boolean;
};

const AddInsightModal: React.FC<Props> = ({
  open,
  selectedType,
  setSelectedType,
  textTemp,
  setTextTemp,
  onAdd,
  onClose,
  loading,
  cardLabels,
  columns,
  selectedMetric,
  setSelectedMetric,
  selectedDimension,
  setSelectedDimension,
  selectedFilterColumn,
  setSelectedFilterColumn,
  selectedFilterValues,
  setSelectedFilterValues,
  selectedImageFile,
  setSelectedImageFile,
  uploadedImageUrl,
  setUploadedImageUrl,
  goToDashboard,
  isBscPinned = false,
}) => {
  const { t } = useTranslation("dashboardModals");
  useEffect(() => {
    setSelectedMetric(undefined);
    setSelectedDimension(undefined);
    setSelectedFilterColumn(undefined);
    setSelectedFilterValues([]);
    setSelectedImageFile(null);
    setUploadedImageUrl("");
  }, [
    selectedType,
    setSelectedMetric,
    setSelectedDimension,
    setSelectedFilterColumn,
    setSelectedFilterValues,
    setSelectedImageFile,
    setUploadedImageUrl,
  ]);

  const isUnnamedColumn = (name: string) =>
    String(name).toLowerCase().startsWith("unnamed:");

  const isMetaColumn = (name: string) => {
    const lower = String(name).toLowerCase();
    return (
      [
        "_id",
        "_uuid",
        "_index",
        "_version",
        "_submitted_by",
        "_submission_time",
        "_submission_date",
      ].includes(lower) || isUnnamedColumn(lower)
    );
  };

  const isNumericColumn = (col: ColumnOption) => {
    const name = String(col.name || "")
      .trim()
      .toLowerCase();
    const dtype = String(col.dtype || "")
      .trim()
      .toLowerCase();

    if (!name) return false;
    if (isMetaColumn(name)) return false;

    if (
      dtype.includes("int") ||
      dtype.includes("float") ||
      dtype.includes("double") ||
      dtype.includes("number") ||
      dtype.includes("numeric") ||
      dtype.includes("decimal")
    ) {
      return true;
    }

    if (col.min !== undefined || col.max !== undefined) {
      return true;
    }

    const samples = col.sample_values || [];
    if (Array.isArray(samples) && samples.length > 0) {
      const validSamples = samples.filter(
        (v) => v !== null && v !== undefined && v !== "",
      );

      if (
        validSamples.length > 0 &&
        validSamples.every((v) => !Number.isNaN(Number(v)))
      ) {
        return true;
      }
    }

    return false;
  };

  const isValidKPIColumn = (col: ColumnOption) => {
    const name = String(col.name || "").toLowerCase();

    return (
      isNumericColumn(col) &&
      !name.includes("id") &&
      !name.includes("timestamp") &&
      !name.includes("time") &&
      !name.includes("date")
    );
  };

  const isProgressLikeColumn = (col: ColumnOption) => {
    const name = String(col.name || "").toLowerCase();

    return (
      isNumericColumn(col) &&
      (name.includes("progress") ||
        name.includes("completion") ||
        name.includes("completed") ||
        name.includes("percent") ||
        name.includes("percentage") ||
        name.includes("rate") ||
        name.includes("status"))
    );
  };
  const isDateColumn = (col: ColumnOption) => {
    const name = String(col.name || "").toLowerCase();
    const dtype = String(col.dtype || "").toLowerCase();

    if (isMetaColumn(name)) return false;

    if (
      dtype.includes("date") ||
      dtype.includes("datetime") ||
      dtype.includes("timestamp")
    ) {
      return true;
    }

    if (
      name.includes("date") ||
      name.includes("time") ||
      name === "start" ||
      name === "end" ||
      name.includes("_time")
    ) {
      return true;
    }

    const samples = col.sample_values || col.unique_values || [];
    if (Array.isArray(samples) && samples.length > 0) {
      const first = samples.find(
        (v) => v !== null && v !== undefined && String(v).trim() !== "",
      );

      if (first) {
        const str = String(first).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) return true;
        if (!Number.isNaN(Date.parse(str)) && Number.isNaN(Number(str))) {
          return true;
        }
      }
    }

    return false;
  };

  const isGeoColumn = (col: ColumnOption) => {
    const name = String(col.name || "").toLowerCase();

    if (isMetaColumn(name)) return false;

    return (
      name.includes("country") ||
      name.includes("city") ||
      name.includes("region") ||
      name.includes("district") ||
      name.includes("province") ||
      name.includes("governorate") ||
      name.includes("state") ||
      name.includes("location") ||
      name.includes("address") ||
      name.includes("lat") ||
      name.includes("lng") ||
      name.includes("lon") ||
      name.includes("longitude") ||
      name.includes("latitude") ||
      name.includes("geo")
    );
  };

  const isSentimentColumn = (col: ColumnOption) => {
    const name = String(col.name || "").toLowerCase();

    return (
      name.includes("sentiment") ||
      name.includes("rating") ||
      name.includes("score")
    );
  };

  const isTextLikeColumn = (col: ColumnOption) => {
    const dtype = String(col.dtype || "").toLowerCase();
    const name = String(col.name || "").toLowerCase();

    if (isMetaColumn(name)) return false;
    if (isNumericColumn(col)) return false;
    if (isDateColumn(col)) return false;
    if (isGeoColumn(col)) return false;

    if (
      dtype.includes("object") ||
      dtype.includes("string") ||
      dtype.includes("text")
    ) {
      return true;
    }

    return (
      name.includes("name") ||
      name.includes("title") ||
      name.includes("description") ||
      name.includes("text") ||
      name.includes("task") ||
      name.includes("project") ||
      name.includes("assigned")
    );
  };

  const numericColumns = useMemo(
    () => columns.filter((col) => isNumericColumn(col) && !isDateColumn(col)),
    [columns],
  );
  const kpiColumns = useMemo(
    () => columns.filter((col) => isValidKPIColumn(col)),
    [columns],
  );

  const progressColumns = useMemo(
    () => columns.filter((col) => isProgressLikeColumn(col)),
    [columns],
  );
  const dateColumns = useMemo(
    () => columns.filter((col) => isDateColumn(col)),
    [columns],
  );

  const geoColumns = useMemo(
    () => columns.filter((col) => isGeoColumn(col)),
    [columns],
  );

  const textColumns = useMemo(
    () => columns.filter((col) => isTextLikeColumn(col)),
    [columns],
  );

  const sentimentColumns = useMemo(
    () => columns.filter((col) => isSentimentColumn(col)),
    [columns],
  );

  const categoricalColumns = useMemo(
    () =>
      columns.filter((col) => {
        if (isMetaColumn(col.name)) return false;
        if (isNumericColumn(col)) return false;
        if (isDateColumn(col)) return false;
        if (isGeoColumn(col)) return false;
        return !!col.unique_values?.length;
      }),
    [columns],
  );

  const filterableColumns = useMemo(
    () =>
      columns.filter((col) => {
        const name = String(col.name || "")
          .trim()
          .toLowerCase();

        if (isMetaColumn(name)) return false;
        if (!col.unique_values || !col.unique_values.length) return false;
        if (name.startsWith("column_")) return false;
        if (name.startsWith("unnamed:")) return false;

        return true;
      }),
    [columns],
  );

  const isCardTypeSupported = (type: CardType) => {
    switch (type) {
      case "text":
      case "image":
        return true;

      case "critical":
        return kpiColumns.length > 0;

      case "progress":
        return progressColumns.length > 0;

      case "histogram":
        return numericColumns.length > 0;

      case "heatmap":
        return numericColumns.length > 0 && categoricalColumns.length > 0;

      case "treemap":
        return numericColumns.length > 0 && categoricalColumns.length >= 2;

      case "geo":
        return geoColumns.length > 0 && numericColumns.length > 0;

      case "location":
        return geoColumns.length > 0;

      case "trend":
      case "anomaly":
        return numericColumns.length > 0 && dateColumns.length > 0;

      case "wordCloud":
        return textColumns.length > 0;

      case "sentiment":
        return sentimentColumns.length > 0;

      default:
        return true;
    }
  };

  const filterValueOptions = useMemo(() => {
    const found = columns.find((col) => col.name === selectedFilterColumn);

    if (!found?.unique_values?.length) return [];

    return found.unique_values.map((value: any) => ({
      label: String(value),
      value: String(value),
    }));
  }, [columns, selectedFilterColumn]);

  const needsMetric = useMemo(() => {
    return selectedType
      ? [
          "critical",
          "trend",
          "progress",
          "anomaly",
          "histogram",
          "treemap",
          "heatmap",
          "geo",
        ].includes(selectedType)
      : false;
  }, [selectedType]);

  const needsDimension = useMemo(() => {
    return selectedType
      ? ["trend", "anomaly", "treemap", "heatmap", "geo"].includes(selectedType)
      : false;
  }, [selectedType]);

  const needsSingleColumn = useMemo(() => {
    return selectedType
      ? ["wordCloud", "sentiment"].includes(selectedType)
      : false;
  }, [selectedType]);

  const showFilterSection = useMemo(() => {
    if (!selectedType) return false;
    if (["text", "image"].includes(selectedType)) return false;
    if (!isCardTypeSupported(selectedType)) return false;

    return true;
  }, [
    selectedType,
    numericColumns,
    categoricalColumns,
    textColumns,
    geoColumns,
    dateColumns,
    sentimentColumns,
    kpiColumns,
    progressColumns,
  ]);

  const metricOptions = useMemo(() => {
    if (!selectedType) return [];

    switch (selectedType) {
      case "critical":
        return kpiColumns;

      case "progress":
        return progressColumns;

      case "histogram":
      case "trend":
      case "anomaly":
      case "heatmap":
      case "treemap":
      case "geo":
        return numericColumns;

      case "sentiment":
        return sentimentColumns;

      default:
        return [];
    }
  }, [
    selectedType,
    numericColumns,
    sentimentColumns,
    kpiColumns,
    progressColumns,
  ]);

  const dimensionOptions = useMemo(() => {
    if (!selectedType) return [];

    switch (selectedType) {
      case "trend":
      case "anomaly":
        return dateColumns;

      case "heatmap":
      case "treemap":
        return categoricalColumns;

      case "geo":
      case "location":
        return geoColumns;

      default:
        return [];
    }
  }, [selectedType, dateColumns, categoricalColumns, geoColumns]);

  const singleColumnOptions = useMemo(() => {
    if (!selectedType) return [];

    switch (selectedType) {
      case "wordCloud":
        return textColumns;

      case "sentiment":
        return sentimentColumns;

      default:
        return [];
    }
  }, [selectedType, textColumns, sentimentColumns]);

  const disableAddButton = useMemo(() => {
    if (!selectedType) return true;

    if (selectedType === "text") {
      return !textTemp.trim();
    }

    if (selectedType === "image") {
      return !selectedImageFile;
    }

    if (!isCardTypeSupported(selectedType)) {
      return true;
    }

    const metricRequiredTypes = [
      "critical",
      "trend",
      "progress",
      "anomaly",
      "histogram",
      "treemap",
      "heatmap",
      "geo",
    ];
    const singleColumnRequiredTypes = ["sentiment", "wordCloud"];
    const dimensionRequiredTypes = [
      "trend",
      "anomaly",
      "treemap",
      "heatmap",
      "geo",
    ];

    if (metricRequiredTypes.includes(selectedType) && !selectedMetric) {
      return true;
    }

    if (singleColumnRequiredTypes.includes(selectedType) && !selectedMetric) {
      return true;
    }

    if (dimensionRequiredTypes.includes(selectedType) && !selectedDimension) {
      return true;
    }

    return false;
  }, [
    selectedType,
    textTemp,
    selectedImageFile,
    selectedMetric,
    selectedDimension,
    numericColumns,
    categoricalColumns,
    textColumns,
    geoColumns,
    dateColumns,
    sentimentColumns,
    kpiColumns,
    progressColumns,
  ]);

  // const TrendAnalysisPreview = () => (
  //   <Card size="small" bodyStyle={{ padding: 14 }} style={{ borderRadius: 12 }}>
  //     <div style={{ display: "flex", justifyContent: "space-between" }}>
  //       <b>Trend Analysis</b>
  //       <span>⋮</span>
  //     </div>

  //     <svg width="100%" height="150" viewBox="0 0 420 150">
  //       <polyline
  //         fill="none"
  //         stroke="#A072EB"
  //         strokeWidth="3"
  //         points="0,120 60,90 120,80 180,110 240,60 300,40 360,70 420,50"
  //       />

  //       <circle cx="300" cy="40" r="5" fill="#A072EB" />
  //       <rect x="285" y="10" width="40" height="20" rx="6" fill="#1f1b3d" />
  //       <text x="305" y="24" fontSize="10" fill="white" textAnchor="middle">
  //         140
  //       </text>
  //     </svg>
  //   </Card>
  // );

  // const AnomalyPreview = () => (
  //   <div>
  //     <Card
  //       size="small"
  //       bodyStyle={{ padding: 14 }}
  //       style={{
  //         borderRadius: 12,
  //         border: "1px solid #eee",
  //         boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  //         marginBottom: 16,
  //       }}
  //     >
  //       <div
  //         style={{
  //           display: "flex",
  //           justifyContent: "space-between",
  //           fontWeight: 700,
  //           fontSize: 15,
  //           marginBottom: 12,
  //           color: "#1f1b3d",
  //         }}
  //       >
  //         <span>Trend/Anomaly Detection</span>
  //         <span style={{ fontSize: 18 }}>⋮</span>
  //       </div>

  //       <svg width="100%" height="150" viewBox="0 0 420 150">
  //         <path
  //           d="M0 95 C30 80, 35 50, 60 70 C90 95, 105 115, 130 85 C160 50, 180 40, 205 62 C230 84, 250 45, 280 58 C310 72, 330 78, 350 92 C370 105, 390 60, 420 70"
  //           fill="none"
  //           stroke="#A072EB"
  //           strokeWidth="3"
  //         />

  //         <path
  //           d="M0 95 C30 80, 35 50, 60 70 C90 95, 105 115, 130 85 C160 50, 180 40, 205 62 C230 84, 250 45, 280 58 C310 72, 330 78, 350 92 C370 105, 390 60, 420 70 L420 140 L0 140 Z"
  //           fill="rgba(160,114,235,0.16)"
  //         />

  //         <rect x="315" y="35" width="78" height="34" rx="6" fill="#ff4d6d" />
  //         <text x="354" y="50" textAnchor="middle" fontSize="10" fill="white">
  //           Anomaly
  //         </text>
  //         <text x="354" y="62" textAnchor="middle" fontSize="10" fill="white">
  //           detected
  //         </text>

  //         <circle cx="397" cy="52" r="5" fill="#ff4d6d" />
  //       </svg>
  //     </Card>

  //     <Text
  //       style={{
  //         display: "block",
  //         fontSize: 14,
  //         color: "#555",
  //         lineHeight: 1.5,
  //         marginBottom: 18,
  //       }}
  //     >
  //       This card displays trends and potential anomalies in survey performance
  //       over time.
  //     </Text>
  //   </div>
  // );

  return (
    <Modal
      title={t("addInsightModal.title")}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" className="btn-1" onClick={onClose}>
          {t("addInsightModal.cancel")}
        </Button>,
        <Button key="go" className="btn-2" onClick={goToDashboard}>
          {t("addInsightModal.goToDashboard")}
        </Button>,
      ]}
      width={980}
      destroyOnClose
      className="add-insight-modalStyle"
    >
      <Row gutter={16}>
        <Col span={10}>
          <List
            dataSource={(Object.keys(cardLabels) as CardType[]).filter(
              (t) => t !== "bsc" || isBscPinned,
            )}
            renderItem={(cardType) => {
              const supported = isCardTypeSupported(cardType);

              return (
                <List.Item
                  style={{
                    border: "1px dashed #8c6cff",
                    borderRadius: 10,
                    padding: 12,
                    marginBottom: 12,
                    cursor: supported ? "pointer" : "not-allowed",
                    background:
                      selectedType === cardType
                        ? "rgba(140,108,255,0.06)"
                        : supported
                          ? "transparent"
                          : "#f5f5f5",
                    opacity: supported ? 1 : 0.5,
                  }}
                  onClick={() => {
                    if (!supported) return;

                    setSelectedType(cardType);
                    setSelectedMetric(undefined);
                    setSelectedDimension(undefined);
                    setSelectedFilterColumn(undefined);
                    setSelectedFilterValues([]);
                  }}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Space>
                      <PlusOutlined /> {cardLabels[cardType].label}
                    </Space>

                    {!supported && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("addInsightModal.notAvailable")}
                      </Text>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />
        </Col>

        <Col span={14}>
          <div
            style={{
              background: "#f7f7ff",
              border: "1px solid #e7e5ff",
              borderRadius: 12,
              padding: 16,
            }}
          >
            {selectedType ? (
              <>
                <div style={{ marginBottom: 12, fontWeight: 600 }}>
                  {cardLabels[selectedType].label}
                </div>

                {!isCardTypeSupported(selectedType) && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: 12,
                      borderRadius: 8,
                      background: "#fff7e6",
                      border: "1px solid #ffd591",
                    }}
                  >
                    <Text style={{ color: "#ad6800" }}>
                      {t("addInsightModal.cardNotAvailableDesc")}
                    </Text>
                  </div>
                )}
                {/* 
                <div style={{ marginBottom: 16 }}>
                  <Card size="small">
                    {cardLabels[selectedType].render({
                      id: "tmp",
                      type: selectedType,
                      title: "",
                    })}
                  </Card>
                </div> */}

                <div style={{ marginBottom: 16 }}>
                  {selectedType === "critical" ? (
                    <CriticalMetricPreview />
                  ) : selectedType === "progress" ? (
                    <ProgressPreview />
                  ) : selectedType === "trend" ? (
                    <TrendAnalysisPreview />
                  ) : selectedType === "anomaly" ? (
                    <AnomalyPreview />
                  ) : selectedType === "histogram" ? (
                    <HistogramPreview />
                  ) : selectedType === "treemap" ? (
                    <TreemapPreview />
                  ) : selectedType === "heatmap" ? (
                    <HeatmapPreview />
                  ) : selectedType === "wordCloud" ? (
                    <WordCloudPreview />
                  ) : selectedType === "geo" ? (
                    <GeoClusterPreview />
                  ) : selectedType === "location" ? (
                    <LocationMapPreview />
                  ) : null}
                </div>
                {/* {selectedType === "trend" ? (
                  <TrendAnalysisPreview />
                ) : selectedType === "anomaly" ? (
                  <AnomalyPreview />
                ) : selectedType !== "image" ? (
                  <Card size="small">
                    {cardLabels[selectedType].render({
                      id: "tmp",
                      type: selectedType,
                      title: "",
                    })}
                  </Card>
                ) : null} */}

                {isCardTypeSupported(selectedType) ? (
                  <Form layout="vertical">
                    {needsMetric && (
                      <Form.Item label={t("addInsightModal.metricColumnLabel")}>
                        <Select
                          allowClear
                          placeholder={
                            selectedType === "critical"
                              ? kpiColumns.length
                                ? t("addInsightModal.selectKpiColumn")
                                : t("addInsightModal.noValidKpiColumns")
                              : selectedType === "progress"
                                ? progressColumns.length
                                  ? t("addInsightModal.selectProgressColumn")
                                  : t(
                                      "addInsightModal.noValidProgressColumns",
                                    )
                                : metricOptions.length
                                  ? t("addInsightModal.selectMetricColumn")
                                  : t(
                                      "addInsightModal.noValidNumericColumns",
                                    )
                          }
                          value={selectedMetric}
                          onChange={setSelectedMetric}
                          options={metricOptions.map((col) => ({
                            label: col.name,
                            value: col.name,
                          }))}
                          disabled={!metricOptions.length}
                        />
                      </Form.Item>
                    )}

                    {needsDimension && (
                      <Form.Item
                        label={t("addInsightModal.dimensionColumnLabel")}
                      >
                        <Select
                          allowClear
                          placeholder={
                            dimensionOptions.length
                              ? t("addInsightModal.selectDimensionColumn")
                              : t(
                                  "addInsightModal.noValidDimensionColumns",
                                )
                          }
                          value={selectedDimension}
                          onChange={setSelectedDimension}
                          options={dimensionOptions.map((col) => ({
                            label: col.name,
                            value: col.name,
                          }))}
                          disabled={!dimensionOptions.length}
                        />
                      </Form.Item>
                    )}

                    {needsSingleColumn && (
                      <Form.Item label={t("addInsightModal.columnLabel")}>
                        <Select
                          allowClear
                          placeholder={
                            singleColumnOptions.length
                              ? t("addInsightModal.selectColumn")
                              : t("addInsightModal.noValidColumns")
                          }
                          value={selectedMetric}
                          onChange={setSelectedMetric}
                          options={singleColumnOptions.map((col) => ({
                            label: col.name,
                            value: col.name,
                          }))}
                          disabled={!singleColumnOptions.length}
                        />
                      </Form.Item>
                    )}

                    {showFilterSection && (
                      <>
                        <Form.Item
                          label={t("addInsightModal.optionalFilterLabel")}
                        >
                          <Select
                            allowClear
                            placeholder={t(
                              "addInsightModal.selectFilterColumnOptional",
                            )}
                            value={selectedFilterColumn}
                            onChange={(value) => {
                              setSelectedFilterColumn(value);
                              setSelectedFilterValues([]);
                            }}
                            options={filterableColumns.map((col) => ({
                              label: col.name,
                              value: col.name,
                            }))}
                          />
                        </Form.Item>

                        <Text
                          type="secondary"
                          style={{
                            display: "block",
                            marginTop: -8,
                            marginBottom: 16,
                          }}
                        >
                          {t("addInsightModal.optionalFilterHint")}
                        </Text>

                        {selectedFilterColumn && (
                          <Form.Item
                            label={t(
                              "addInsightModal.filterValuesOptionalLabel",
                            )}
                          >
                            <Select
                              mode="multiple"
                              allowClear
                              placeholder={t(
                                "addInsightModal.selectFilterValues",
                              )}
                              value={selectedFilterValues}
                              onChange={setSelectedFilterValues}
                              options={filterValueOptions}
                              disabled={!filterValueOptions.length}
                            />
                          </Form.Item>
                        )}
                      </>
                    )}

                    {selectedType === "text" && (
                      <Form.Item label={t("addInsightModal.contentLabel")}>
                        <Input.TextArea
                          value={textTemp}
                          onChange={(e) => setTextTemp(e.target.value)}
                          autoSize={{ minRows: 3 }}
                        />
                      </Form.Item>
                    )}

                    {selectedType === "image" && (
                      <>
                        <label
                          htmlFor="dashboard-image-upload"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            height: 64,
                            border: "1.5px dashed #A072EB",
                            borderRadius: 8,
                            color: "#4A1C96",
                            fontFamily: "Poppins",
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: "pointer",
                            marginBottom: 16,
                          }}
                        >
                          <PlusOutlined />
                          {selectedImageFile
                            ? selectedImageFile.name
                            : t("addInsightModal.uploadImageLabel")}
                        </label>

                        <input
                          id="dashboard-image-upload"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setSelectedImageFile(file);

                            if (file) {
                              const previewUrl = URL.createObjectURL(file);
                              setUploadedImageUrl(previewUrl);
                            } else {
                              setUploadedImageUrl("");
                            }
                          }}
                        />

                        <Text
                          type="secondary"
                          style={{
                            display: "block",
                            fontFamily: "Poppins",
                            fontSize: 14,
                            marginBottom: uploadedImageUrl ? 12 : 0,
                          }}
                        >
                          {t("addInsightModal.addImageHint")}
                        </Text>

                        {uploadedImageUrl && (
                          <div style={{ marginTop: 12 }}>
                            <img
                              src={uploadedImageUrl}
                              alt="Preview"
                              style={{
                                width: "100%",
                                maxHeight: 180,
                                borderRadius: 8,
                                objectFit: "cover",
                                border: "1px solid #eee",
                                display: "block",
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {!columns.length && (
                      <Text type="secondary">
                        {t("addInsightModal.noColumnsAvailable")}
                      </Text>
                    )}

                    <div style={{ marginTop: 20, textAlign: "center" }}>
                      <Button
                        onClick={onAdd}
                        disabled={disableAddButton}
                        loading={loading}
                        className="add-to-dashboard-footerButton"
                      >
                        {t("addInsightModal.addToDashboard")}
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <Button disabled className="add-to-dashboard-footerButton">
                      {t("addInsightModal.addToDashboard")}
                    </Button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default React.memo(AddInsightModal);
